import { useState } from 'react'
import Modal from '../../components/Modal'
import Boton from '../../components/Boton'
import { npApi } from '../../api/compras'
import { colors, text, space, font, radius } from '../../design/tokens'
import { nuevoUuid, st } from '../emp900/_helpers'
import { CajaError, Campo } from '../emp900/_comun'
import { MOTIVO_CIERRE, fmtCant } from './_np'

/**
 * Modales del ciclo de la Nota de Pedido, compartidos por la pantalla Pedidos
 * (Anulación / Cancelación) y por el detalle. `accion(fn, despues)` la provee
 * quien los abre: ejecuta, recarga y cierra.
 */

/** Emisión ≠ envío (§3.4): tres vías; en las tres queda registrado el PDF exacto, canal, destinatario y quién. */
export function EnviarModal({ p, trabajando, error, onClose, accion }) {
  const [email, setEmail] = useState(p.proveedor_lgr?.email || '')
  const [telefono, setTelefono] = useState(p.proveedor_lgr?.telefono || '')
  const [mensaje, setMensaje] = useState('')
  const via = { border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.lg, marginBottom: space.md }
  const viaTitulo = { fontWeight: 600, fontSize: text.body, color: colors.text, marginBottom: space.md }
  return (
    <Modal title={`Enviar ${p.numero} al proveedor`} onClose={onClose} maxWidth="560px">
      <div style={via}>
        <div style={viaTitulo}>✉ Por mail (PDF adjunto)</div>
        <Campo label="Para"><input style={st.input} value={email} onChange={e => setEmail(e.target.value)} placeholder="compras@proveedor.com" /></Campo>
        <Campo label="Mensaje (opcional)"><textarea style={st.textarea} rows={2} value={mensaje} onChange={e => setMensaje(e.target.value)} /></Campo>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: space.md }}>
          <Boton variante="send" disabled={trabajando || !email} onClick={() => accion(() => npApi.enviar(p.id, { op_uuid: nuevoUuid(), canal: 'email', email, mensaje: mensaje || undefined }))}>{trabajando ? 'Enviando…' : '✉ Enviar mail'}</Boton>
        </div>
      </div>
      <div style={via}>
        <div style={viaTitulo}>📱 Por WhatsApp (link al PDF)</div>
        <Campo label="Teléfono (opcional: abre el chat con ese número)"><input style={st.input} value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="11 5555 1234" /></Campo>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: space.md }}>
          <Boton variante="send" disabled={trabajando} onClick={() => accion(() => npApi.enviar(p.id, { op_uuid: nuevoUuid(), canal: 'whatsapp', telefono: telefono || undefined, mensaje: mensaje || undefined }), r => r?.data?.wa_link && window.open(r.data.wa_link, '_blank'))}>📱 Abrir WhatsApp</Boton>
        </div>
      </div>
      <div style={via}>
        <div style={{ display: 'flex', alignItems: 'center', gap: space.lg }}>
          <div style={{ flex: 1 }}><div style={{ ...viaTitulo, marginBottom: 0 }}>🤝 Impreso / por otra vía</div><div style={{ fontSize: text.sec, color: colors.textSec }}>Descarga el reporte y deja registrado el envío.</div></div>
          <Boton variante="secondary" disabled={trabajando} onClick={() => accion(() => npApi.enviar(p.id, { op_uuid: nuevoUuid(), canal: 'manual' }), () => npApi.descargarPdf(p.id, p.numero))}>⬇ PDF y registrar</Boton>
        </div>
      </div>
      {error && <CajaError>{error}</CajaError>}
    </Modal>
  )
}

/** CANCELACIÓN (camino de Martin): el saldo pendiente de cada renglón marcado se cancela con motivo. El pedido sigue vivo para el resto. */
export function CancelarSaldoModal({ p, trabajando, error, onClose, accion }) {
  const conSaldo = (p.items || []).filter(it => Number(it.saldo) > 0)
  const [sel, setSel] = useState(() => new Set(conSaldo.map(it => it.id)))
  const [motivo, setMotivo] = useState('proveedor_sin_stock')
  const [comentario, setComentario] = useState('')
  const toggle = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const listo = sel.size > 0 && (motivo !== 'otro' || comentario.trim())
  return (
    <Modal title={`Cancelar saldo pendiente — ${p.numero}`} onClose={onClose} maxWidth="640px">
      <p style={{ ...st.info, marginTop: 0 }}>Lo que no va a llegar se cancela con motivo. Lo ya recibido no se toca. Cuando ningún renglón tiene saldo, el pedido queda cerrado y el faltante registrado para el cumplimiento del proveedor.</p>
      <table style={st.table}>
        <thead><tr><th style={st.th} /><th style={st.th}>Artículo</th><th style={{ ...st.th, textAlign: 'right' }}>Pedido</th><th style={{ ...st.th, textAlign: 'right' }}>Recibido</th><th style={{ ...st.th, textAlign: 'right' }}>Saldo a cancelar</th></tr></thead>
        <tbody>
          {conSaldo.map(it => (
            <tr key={it.id} style={st.trClick} onClick={() => toggle(it.id)}>
              <td style={st.td}><input type="checkbox" checked={sel.has(it.id)} onChange={() => toggle(it.id)} onClick={e => e.stopPropagation()} /></td>
              <td style={st.td}>{it.descripcion}<div style={{ fontFamily: font.mono, fontSize: text.sec, color: colors.textMeta }}>{it.sku}</div></td>
              <td style={st.tdImporte}>{fmtCant(it.cantidad)}</td><td style={st.tdImporte}>{fmtCant(it.cantidad_recibida)}</td><td style={st.tdImporte}>{fmtCant(it.saldo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ ...st.grid2, marginTop: space.md }}>
        <Campo label="Motivo"><select style={st.input} value={motivo} onChange={e => setMotivo(e.target.value)}>{Object.entries(MOTIVO_CIERRE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></Campo>
        <Campo label={motivo === 'otro' ? 'Comentario (obligatorio)' : 'Comentario'}><input style={st.input} value={comentario} onChange={e => setComentario(e.target.value)} /></Campo>
      </div>
      {error && <CajaError>{error}</CajaError>}
      <div style={st.pie}>
        <Boton variante="ghost" onClick={onClose}>Volver</Boton>
        <Boton variante="primary" disabled={!listo || trabajando} onClick={() => accion(() => npApi.cancelarSaldo(p.id, { op_uuid: nuevoUuid(), lineas: [...sel].map(item_id => ({ item_id, motivo, comentario: comentario || undefined })) }))}>{trabajando ? 'Cancelando…' : `Cancelar saldo de ${sel.size} renglón(es)`}</Boton>
      </div>
    </Modal>
  )
}

/** ANULACIÓN: el pedido entero se da de baja (borrador, o emitido sin recepciones). Nada se borra: queda anulado con su motivo. */
export function AnularModal({ p, trabajando, error, onClose, accion }) {
  const [motivo, setMotivo] = useState('')
  return (
    <Modal title={`Anular ${p.numero}`} onClose={onClose} maxWidth="480px">
      <p style={{ ...st.info, marginTop: 0 }}>Se anula el pedido completo. Nada se borra: queda anulado con su motivo, a la vista.</p>
      <Campo label="Motivo"><input style={st.input} value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus /></Campo>
      {error && <CajaError>{error}</CajaError>}
      <div style={st.pie}>
        <Boton variante="ghost" onClick={onClose}>Volver</Boton>
        <Boton variante="destructive" disabled={motivo.trim().length < 3 || trabajando} onClick={() => accion(() => npApi.anular(p.id, { op_uuid: nuevoUuid(), motivo }))}>{trabajando ? 'Anulando…' : 'Anular pedido'}</Boton>
      </div>
    </Modal>
  )
}
